import { useState, useMemo, useCallback, useEffect } from 'react'
import { processChartData } from './utils'
import { getChartOptions, getChartDataConfig } from './chartConfig'
import { EnhancementReportStat } from '../../../../models/enhancement/enhancement-report-stats'
import { ChartWrapper, ChartViewLevelSelector, useChart, useChartError } from '../common'
import { EnhancementViewLevel } from '../../ReportViewLevelSelector'

interface SubmittedVsOverwrittenChartProps {
  data: EnhancementReportStat[]
  isLoading: boolean
  selectedRequestTypes?: string[] | null
}

interface ChartData {
  departments: string[]
  submittedEnhancements: number[]
  overwrittenEnhancements: number[]
  overwrittenPercentages: number[]
}

function SubmittedVsOverwrittenChart({
  data,
  isLoading,
  selectedRequestTypes
}: SubmittedVsOverwrittenChartProps) {
  const chartRef = useChart()
  const { error, handleError, setError } = useChartError()
  
  const [viewLevel, setViewLevel] = useState<EnhancementViewLevel>(() => {
    try {
      const stored = localStorage.getItem('enhancement-submitted-vs-overwritten-view-level')
      if (stored && Object.values(EnhancementViewLevel).includes(stored as EnhancementViewLevel)) {
        return stored as EnhancementViewLevel
      }
    } catch (err) {
      console.warn('Failed to read view level from localStorage:', err)
    }
    return EnhancementViewLevel.DEPARTMENT
  })
  
  const handleViewLevelChange = useCallback((newLevel: EnhancementViewLevel) => {
    setViewLevel(prevLevel => {
      if (prevLevel !== newLevel) {
        try {
          localStorage.setItem('enhancement-submitted-vs-overwritten-view-level', newLevel)
        } catch (err) {
          console.warn('Failed to save view level to localStorage:', err)
        }
      }
      return newLevel
    })
  }, [])

  const hasRequiredRequestTypes = useMemo(() => {
    const types = selectedRequestTypes ?? []

    return types.length === 0 ? true : types.some(type => type.toLowerCase().includes('call out'))

  }, [selectedRequestTypes])
  
  const [chartData, setChartData] = useState<ChartData>({
    departments: [],
    submittedEnhancements: [],
    overwrittenEnhancements: [],
    overwrittenPercentages: []
  })
  
  useEffect(() => {
    try {
      if (!data || data.length === 0) {
        setChartData({
          departments: [],
          submittedEnhancements: [],
          overwrittenEnhancements: [],
          overwrittenPercentages: []
        })
        return
      }
      
      const processedData = processChartData(data, selectedRequestTypes, viewLevel)
      setChartData(processedData)
      setError(null)
    } catch (err) {
      handleError(err, 'processing chart data')
      setChartData({
        departments: [],
        submittedEnhancements: [],
        overwrittenEnhancements: [],
        overwrittenPercentages: []
      })
    }
  }, [data, selectedRequestTypes, viewLevel, handleError, setError])

  const chartOptions = useMemo(() =>
      getChartOptions(chartData.overwrittenPercentages, viewLevel),
    [chartData.overwrittenPercentages, viewLevel]
  )
  
  const chartDataConfig = useMemo(() =>
    getChartDataConfig(
      chartData.departments,
      chartData.submittedEnhancements,
      chartData.overwrittenEnhancements,
      chartData.overwrittenPercentages
    ),
    [chartData]
  )
  
  const headerAction = useMemo(() => (
    <ChartViewLevelSelector
      value={viewLevel}
      onViewLevelChange={handleViewLevelChange}
    />
  ), [viewLevel, handleViewLevelChange])

  const hasData = Boolean(data?.length && hasRequiredRequestTypes)
  const emptyStateMessage = !hasRequiredRequestTypes
    ? 'This chart requires "Call Out" request type to be selected'
    : 'No data available for the selected filters'

  const chartTitle = `Enhancements Submitted vs Overwritten by ${viewLevel}`

  return (
    <ChartWrapper
      ref={chartRef}
      title={chartTitle}
      headerAction={headerAction}
      error={error}
      isLoading={isLoading}
      hasData={hasData}
      emptyStateMessage={emptyStateMessage}
      chartType="bar"
      chartData={chartDataConfig}
      chartOptions={chartOptions}
      height={500}
    />
  )
}

export default SubmittedVsOverwrittenChart